import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../AuthProvider';
import { useNavigate } from 'react-router-dom';
import AdminNav from './AdminNav';

const SynopsisPromptPage = () => {
  const { loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [constants, setConstants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingRole, setCheckingRole] = useState(true);

  // Edit constant state
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const checkAdminRole = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }

        // Check for admin or PM role
        const userRole = session.user.app_metadata?.role || session.user.role;
        if (userRole === 'admin' || userRole === 'pm') {
          setIsAdmin(true);
          fetchConstants();
        } else {
          setIsAdmin(false);
          setError('Access denied. Only admin and PM can access this page.');
        }
      } catch (err) {
        console.error('Error checking admin role:', err);
        setError('Failed to verify permissions.');
      } finally {
        setCheckingRole(false);
      }
    };

    if (!authLoading) {
      checkAdminRole();
    }
  }, [authLoading, navigate]);

  const fetchConstants = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('admin_constants')
        .select('*')
        .order('key');

      if (error) throw error;
      
      // Filter only evaluation prompts
      const evaluationPrompts = (data || []).filter(c => 
        c.key.startsWith('OFFICIAL_EVALUATION_') || c.key === 'COMPARISON_EVALUATION_PROMPT'
      );
      
      // Custom sorting logic
      const sortedData = evaluationPrompts.sort((a, b) => {
        const priority = {
          'OFFICIAL_EVALUATION_SYSTEM_PROMPT': 1,
          'OFFICIAL_EVALUATION_USER_PROMPT': 2,
          'COMPARISON_EVALUATION_PROMPT': 999
        };
        
        const pA = priority[a.key] || 500;
        const pB = priority[b.key] || 500;
        
        if (pA !== pB) return pA - pB;
        return a.key.localeCompare(b.key);
      });

      setConstants(sortedData);
    } catch (err) {
      console.error('Error fetching constants:', err);
      setError('Failed to load constants.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateConstant = async (id) => {
    try {
      setIsSaving(true);
      const { error } = await supabase
        .from('admin_constants')
        .update({ value: editValue, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      
      setEditingId(null);
      setEditValue('');
      fetchConstants();
    } catch (err) {
      console.error('Error updating constant:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const startEditing = (constant) => {
    setEditingId(constant.id);
    setEditValue(constant.value);
  };

  const TokenReference = ({ isOfficial }) => (
    <div className="mt-4 bg-muted/30 border border-border rounded-lg p-4">
      <h3 className="text-foreground text-sm font-semibold mb-2 flex items-center">
        <svg className="w-4 h-4 mr-2 text-primary" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
        Prompt Token Reference
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="text-muted-foreground text-xs">
          <code className="bg-muted px-1 py-0.5 rounded font-bold text-foreground">{'{reference_text}'}</code>
          <p className="mt-1">The human-verified reference text used as the standard for comparison.</p>
        </div>
        <div className="text-muted-foreground text-xs">
          <code className="bg-muted px-1 py-0.5 rounded font-bold text-foreground">{'{generated_text}'}</code>
          <p className="mt-1">The AI-generated synopsis or output that needs to be evaluated.</p>
        </div>
        <div className="text-muted-foreground text-xs">
          <code className="bg-muted px-1 py-0.5 rounded font-bold text-foreground">{'{context}'}</code>
          <p className="mt-1">Metadata like video title, description, or specific comparison criteria.</p>
        </div>
      </div>
      {isOfficial && (
        <div className="mt-4 pt-4 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="text-muted-foreground text-xs">
            <h4 className="text-foreground font-bold mb-1 uppercase tracking-wider text-[10px]">Official Evaluation Layer</h4>
            <p>The <code className="bg-muted px-1 rounded text-foreground">OFFICIAL_EVALUATION</code> prompts utilize the Story Editor pipeline. They support all tokens above and specifically require <code className="bg-muted px-1 rounded text-foreground">{'{generated_text}'}</code> for analysis.</p>
          </div>
        </div>
      )}
    </div>
  );

  if (authLoading || checkingRole) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-foreground text-xl font-semibold">Checking permissions...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background p-4 text-center">
        <h1 className="text-destructive text-2xl font-bold mb-4">Access Denied</h1>
        <p className="text-muted-foreground mb-6">{error || 'Only admin and PM can view this page.'}</p>
        <button 
          onClick={() => navigate('/')}
          className="bg-primary text-primary-foreground hover:opacity-90 px-6 py-2 rounded-lg font-medium transition-opacity"
        >
          Return to Home
        </button>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-100 p-8">
      <div className="max-w-6xl mx-auto pb-12">
        <div className="flex justify-between items-center mb-8">
          <div className="flex flex-col">
            <h1 className="text-3xl font-medium text-foreground">Admin Panel</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Manage evaluation prompts for synopsis generation and comparison.
            </p>
          </div>
          <button 
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground text-sm font-medium whitespace-nowrap border border-border rounded-lg px-3 py-1.5 hover:bg-muted/30 transition-colors"
          >
            ← Back to App
          </button>
        </div>

        <AdminNav />

        {/* Constants List */}
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-foreground"> </h2>
            <button 
              onClick={fetchConstants}
              className="text-primary hover:text-foreground text-sm font-medium"
            >
              Refresh
            </button>
          </div>
          
          {loading ? (
            <div className="bg-card rounded-lg shadow-subtle-sm p-8 text-center text-muted-foreground border border-border">
              Loading prompts...
            </div>
          ) : constants.length === 0 ? (
            <div className="bg-card rounded-lg shadow-subtle-sm p-8 text-center text-muted-foreground border border-border">
              No prompts found.
            </div>
          ) : (
            <>
              {/* Official Evaluation Prompts Group */}
              {constants.some(c => c.key.startsWith('OFFICIAL_EVALUATION_')) && (
                <div className="bg-card rounded-lg shadow-subtle-sm border border-border overflow-hidden mb-8">
                  <div className="px-6 py-4 border-b border-border bg-muted flex justify-between items-center">
                    <h3 className="text-lg font-bold text-primary-foreground">Official Evaluation Prompts</h3>
                    <a 
                      href="https://docs.google.com/spreadsheets/d/1_i19rlc7UrqNf7NvGfJ0f6h3c6-8wvUBjvQ5nVWZ4vY/edit?gid=531701562#gid=531701562"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-muted/50 hover:bg-muted text-foreground text-xs font-medium px-3 py-1.5 rounded-lg border border-border transition-colors"
                    >
                      Check Google Sheet
                    </a>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left table-fixed">
                      <thead className="bg-muted/30 text-foreground text-xs uppercase border-b border-border">
                        <tr>
                          <th className="px-6 py-3 font-semibold w-1/4">Prompt Type</th>
                          <th className="px-6 py-3 font-semibold w-2/3">Value</th>
                          <th className="px-6 py-3 font-semibold text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {constants
                          .filter(c => c.key.startsWith('OFFICIAL_EVALUATION_'))
                          .map((c) => (
                            <tr key={c.id} className="hover:bg-muted/30/50 transition-colors">
                              <td className="px-6 py-4 align-top">
                                <span className="font-mono text-xs font-bold text-foreground break-words">{c.key}</span>
                                {c.description && <p className="mt-2 text-[10px] text-muted-foreground italic uppercase">{c.description}</p>}
                              </td>
                              <td className="px-6 py-4 align-top">
                                {editingId === c.id ? (
                                  <div className="space-y-3">
                                    <textarea
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:outline-none min-h-[300px] font-mono text-sm"
                                    />
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => handleUpdateConstant(c.id)}
                                        disabled={isSaving}
                                        className="bg-primary text-primary-foreground hover:opacity-90 px-4 py-2 rounded text-sm font-medium transition-colors"
                                      >
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                      </button>
                                      <button
                                        onClick={() => setEditingId(null)}
                                        className="bg-muted text-muted-foreground hover:bg-muted/80 px-4 py-2 rounded text-sm font-medium transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="whitespace-pre-wrap font-mono text-sm bg-muted/30 p-4 rounded-lg border border-border max-h-[500px] overflow-y-auto text-foreground leading-relaxed shadow-inner">
                                    {c.value}
                                  </div>
                                )}
                                <div className="mt-4">
                                  <TokenReference isOfficial={c.key === 'OFFICIAL_EVALUATION_USER_PROMPT'} />
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right align-top">
                                {editingId !== c.id && (
                                  <button
                                    onClick={() => startEditing(c)}
                                    className="text-primary hover:text-foreground text-sm font-medium"
                                  >
                                    Edit
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* LLM Default Evaluation Group */}
              {constants.some(c => c.key === 'COMPARISON_EVALUATION_PROMPT') && (
                <div className="bg-card rounded-lg shadow-subtle-sm border border-border overflow-hidden mb-8">
                  <div className="px-6 py-4 border-b border-border bg-primary flex justify-between items-center">
                    <h3 className="text-lg font-bold text-primary-foreground">LLM Default Evaluation Prompts</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left table-fixed">
                      <thead className="bg-muted/30 text-foreground text-xs uppercase border-b border-border">
                        <tr>
                          <th className="px-6 py-3 font-semibold w-1/4">Prompt Type</th>
                          <th className="px-6 py-3 font-semibold w-2/3">Value</th>
                          <th className="px-6 py-3 font-semibold text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {constants
                          .filter(c => c.key === 'COMPARISON_EVALUATION_PROMPT')
                          .map((c) => (
                            <tr key={c.id} className="hover:bg-muted/30/50 transition-colors">
                              <td className="px-6 py-4 align-top">
                                <span className="font-mono text-xs font-bold text-foreground break-words">{c.key}</span>
                                {c.description && <p className="mt-2 text-[10px] text-muted-foreground italic uppercase">{c.description}</p>}
                              </td>
                              <td className="px-6 py-4 align-top">
                                {editingId === c.id ? (
                                  <div className="space-y-3">
                                    <textarea
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:outline-none min-h-[300px] font-mono text-sm"
                                    />
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => handleUpdateConstant(c.id)}
                                        disabled={isSaving}
                                        className="bg-primary text-primary-foreground hover:opacity-90 px-4 py-2 rounded text-sm font-medium transition-colors"
                                      >
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                      </button>
                                      <button
                                        onClick={() => setEditingId(null)}
                                        className="bg-muted text-muted-foreground hover:bg-muted/80 px-4 py-2 rounded text-sm font-medium transition-colors"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="whitespace-pre-wrap font-mono text-sm bg-muted/30 p-4 rounded-lg border border-border max-h-[500px] overflow-y-auto text-foreground leading-relaxed shadow-inner">
                                    {c.value}
                                  </div>
                                )}
                                <div className="mt-4">
                                  <TokenReference isOfficial={false} />
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right align-top">
                                {editingId !== c.id && (
                                  <button
                                    onClick={() => startEditing(c)}
                                    className="text-primary hover:text-foreground text-sm font-medium"
                                  >
                                    Edit
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        
        <div className="mt-8 text-center text-muted-foreground text-sm">
          <p>Note: This is a temporary admin test page. Access is restricted via Supabase RLS.</p>
        </div>
      </div>
    </div>
  );
};

export default SynopsisPromptPage;
